import GameList from '@/components/GameList';
import NewGamesByTaxonomyToolbar from '@/components/NewGamesByTaxonomyToolbar';
import Pod from '@/components/Pod';
import ShowMoreButton from '@/components/ShowMoreButton';
import GamesModel, { ExtendedGameData } from '@/lib/drupal/models/Games';
import { TaxonomyStub } from '@/lib/drupal/models/Taxonomies';
import { useDailyGamesByCriteria, DailySearchConfig, DailySearchCriteriaActionType } from '@/lib/hooks/useDailyGamesByCriteria';
import { useEffectAfterFirstRender } from '@/lib/hooks/useEffectAfterFirstRender';
import { faCircleArrowRight } from '@fortawesome/free-solid-svg-icons';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { IntrinsicProps, createComponent, Slot, ButtonLink, If, Flex, IconFA } from 'react-commons';
import UAParser from 'ua-parser-js';

interface NewGamesByTaxonomyPodProps extends IntrinsicProps {
  initialGames: ExtendedGameData[]
  initialCriteria: Omit<DailySearchConfig, 'method'>
}

export default createComponent<NewGamesByTaxonomyPodProps>('NewGamesByTaxonomyPod', {}, function NewGamesByTaxonomyPod ({ slots }, props) {
  const {
    games,
    isLoading,
    hasReachedEnd,
    showMoreGames,
    updateSearch,
    newSearch,
    criteria,
  } = useDailyGamesByCriteria({
    initialGames: props.initialGames,
    initialCriteria: {
      ...props.initialCriteria,
      method: async (
        limit: number, 
        offset: number, 
        criteria: Omit<DailySearchConfig, 'method'>, 
        sortOrder: 'ASC' | 'DESC', 
      ) => {
        const parser = new UAParser(window.navigator.userAgent || '');
        const parserResults = parser.getResult();
        const mobileOnly = parserResults.device.type === 'mobile';

        const result = await GamesModel.dailySearchAllGames(limit, offset, criteria, sortOrder, mobileOnly);

        return {
          games: result.games as ExtendedGameData[],
          pager: result.pager
        };
      }
    }
  });

  const router = useRouter();

  useEffectAfterFirstRender(() => {
    newSearch();
  }, [ props.initialCriteria, props.initialGames, router.asPath ]);
  
  return (
    <Pod>
      <Slot name='title'>
        {slots.title}
      </Slot>
      <Slot name='toolbar'>
        <NewGamesByTaxonomyToolbar 
          sortBy={criteria.sortBy}
          setSortBy={(nextSort) => {
            updateSearch(
              DailySearchCriteriaActionType.SET_SORT_BY,
              nextSort
            );
          }}
        />
      </Slot>
      {
        If(games.length === 0, () => (
          <Flex alignCenter justifyCenter className='--padMd2'>
            <div><strong>There's nothing here...</strong></div>
          </Flex>
        )).Else(() => (
          <>
            <GameList
              games={games}
              isDownloadGame={criteria.contentTypes.includes('paid_game')}
            />
          </>
        )).EndIf()
      }
      <Slot name='footer'>
        <ShowMoreButton
          isLoading={isLoading}
          hasReachedEnd={hasReachedEnd}
          showMore={showMoreGames}
        >
          <ButtonLink primary small href={`/${criteria.contentTypes.includes('paid_game') ? 'download' : 'online'}/all-games`} className='--iconRight'>
            See All Games
            <IconFA icon={faCircleArrowRight} />
          </ButtonLink>
        </ShowMoreButton>
      </Slot>
    </Pod>
  );
});
